import React, { useState, useEffect } from "react";
import { Menu, Button, Drawer, Avatar, Dropdown } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LogoutOutlined,
  MenuOutlined,
  HomeOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { PiTrafficSignal } from "react-icons/pi";
import { GiTrafficLightsGreen } from "react-icons/gi";
import { BsSignpost, BsFileEarmarkBarGraph, BsBuildings } from "react-icons/bs";
import { ImRoad } from "react-icons/im";
import { getModulesOrder } from "./apis";
import LanguageSwitcher from "./components/LanguageSwitcher";
import TraffixLogo from "./Assets/TraffixLogo.png";

// Module definitions with their display names, routes, and permissions
const MODULE_DEFINITIONS = {
  Home: {
    key: "/",
    icon: <HomeOutlined />,
    label: "Home",
    permissions: "all",
    route: "/",
  },
  Semafix: {
    key: "/semafix",
    icon: <PiTrafficSignal />,
    label: "SemaFix",
    permissions: "semafix",
    route: "/semafix",
    clientHidden: true,
  },
  onde_verde: {
    key: "/onde_verde",
    icon: <GiTrafficLightsGreen />,
    label: "SyncFix",
    permissions: "onde_verde",
    route: "/onde_verde",
    clientHidden: true,
  },
  betwin: {
    key: "/betwin",
    icon: <BsBuildings />,
    label: "BeTwin | Progetto Raise Spoke 4",
    permissions: "betwin",
    route: "/betwin",
    adminOnly: true,
  },
  AIWayport: {
    key: "/AIWayport",
    icon: <BsBuildings />,
    label: "AIWayport",
    permissions: "AIWayport",
    route: "/AIWayport",
    adminOnly: true,
  },
  Lavagna: {
    key: "/Lavagna",
    icon: <BsBuildings />,
    label: "Lavagna",
    permissions: "Lavagna",
    route: "/Lavagna",
    adminOnly: true,
  },
  traffix_all_in_one: {
    key: "/traffix_all_in_one",
    icon: <img src={TraffixLogo} alt="Traffix Logo" style={{ width: '90px', height: '60px', objectFit: 'contain', verticalAlign: 'middle' }} />,
    label: "",
    permissions: "traffix_all_in_one",
    route: "/traffix_all_in_one",
  },
  signal_fix: {
    key: "/signal_fix",
    icon: <BsSignpost />,
    label: "SignalFix",
    permissions: "signal_fix",
    route: "/signal_fix",
    clientHidden: true,
  },
  mystery_client: {
    key: "/mystery_client",
    icon: <BsFileEarmarkBarGraph />,
    label: "MysteryX",
    permissions: "mystery_client",
    route: "/mystery_client",
    clientHidden: true,
  },
  sim_fix: {
    key: "/sim_fix",
    icon: <ImRoad />,
    label: "SimFix",
    permissions: "sim_fix",
    route: "/sim_fix/confronto",
    clientHidden: true,
  },
  analysis: {
    key: "/analysis",
    icon: <BsBuildings />,
    label: "Analysis",
    permissions: "analysis",
    route: "/analysis",
    adminOnly: true,
  },
};

const Navbar = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [moduleOrder, setModuleOrder] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/auth");
    window.location.reload();
  };

  // Fetch module order from API
  useEffect(() => {
    const fetchModuleOrder = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getModulesOrder();
        setModuleOrder(response.modules || []);
      } catch (error) {
        console.error("Error fetching module order:", error);
        // Fallback to default order if API fails
        setModuleOrder(Object.keys(MODULE_DEFINITIONS));
      } finally {
        setLoading(false);
      }
    };

    fetchModuleOrder();
  }, [user]);

  // Home menu item - always visible
  const homeItem = [
    {
      key: "/",
      icon: <HomeOutlined />,
      label: <Link to="/">Home</Link>,
      permissions: "all",
      route: "/",
    },
  ];

  // Create dynamic module items based on module order from database
  const getModuleItems = () => {
    // If not authenticated or still loading, no module items
    if (loading || !user) {
      return [];
    }

    const userPermissions = user.permissions || [];

    // Create ordered menu items based on database order
    return moduleOrder
      .map((moduleName) => {
        const moduleDef = MODULE_DEFINITIONS[moduleName];
        if (!moduleDef) return null;

        // Check if user has permission for this module (excluding Home)
        const hasPermission =
          moduleDef.permissions === "all" ||
          userPermissions.includes(moduleDef.permissions) ||
          user.is_super_admin;

        // Admin-only modules are hidden from regular (non-admin) users
        const isAdminUser = user.is_admin || user.is_super_admin;
        if (moduleDef.adminOnly && !isAdminUser) return null;

        // clientHidden modules are hidden from non-admin (client) users
        if (moduleDef.clientHidden && !isAdminUser) return null;

        if (!hasPermission || moduleDef.key === "/") return null;

        return {
          key: moduleDef.key,
          icon: moduleDef.icon,
          label: <Link to={moduleDef.route}>{moduleDef.label}</Link>,
          permissions: moduleDef.permissions,
        };
      })
      .filter((item) => item != null); // Remove null entries
  };

  const moduleItems = getModuleItems();
  const hasFewItems = moduleItems.length <= 2;

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: <Link to="/settings">Profile</Link>,
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      onClick: handleLogout,
      danger: true,
    },
  ];

  const MenuContent = ({ isMobile = false }) => (
    <div className={`navbar-content ${isMobile ? "mobile" : "desktop"} ${!isMobile && hasFewItems ? "few-items" : ""}`}>
      {/* Home menu - always on the left */}
      <Menu
        theme="dark"
        mode={isMobile ? "vertical" : "horizontal"}
        selectedKeys={[location.pathname]}
        items={homeItem}
        className="home-menu"
        onClick={() => isMobile && setMobileMenuVisible(false)}
      />
      {/* Module items - centered when few */}
      {moduleItems.length > 0 && (
        <Menu
          theme="dark"
          mode={isMobile ? "vertical" : "horizontal"}
          selectedKeys={[location.pathname]}
          items={moduleItems}
          className="main-menu"
          onClick={() => isMobile && setMobileMenuVisible(false)}
        />
      )}

      <div className="navbar-actions">
        <LanguageSwitcher />
        {user && (
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            arrow={{ pointAtCenter: true }}
          >
            <div className="user-profile">
              <Avatar
                src={user?.profile_image || user?.avatar}
                icon={
                  !user?.profile_image && !user?.avatar ? (
                    <UserOutlined />
                  ) : undefined
                }
                className="navbar-user-avatar"
                style={{
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  minHeight: 28,
                  lineHeight: "28px",
                }}
              />
              <span className="user-name">{user.first_name}</span>
            </div>
          </Dropdown>
        )}

        {isMobile && (
          <Button
            type="primary"
            danger
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            className="mobile-logout-btn"
          >
            Logout
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Navigation */}
      <div className="navbar-container desktop-navbar">
        <MenuContent />
      </div>

      {/* Mobile Navigation */}
      <div className="navbar-container mobile-navbar">
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setMobileMenuVisible(true)}
          className="mobile-menu-trigger"
        />

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <LanguageSwitcher />
          {user && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar
                src={user?.profile_image || user?.avatar}
                icon={
                  !user?.profile_image && !user?.avatar ? (
                    <UserOutlined />
                  ) : undefined
                }
                className="navbar-mobile-avatar"
                style={{
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  minHeight: 28,
                  lineHeight: "28px",
                }}
              />
            </Dropdown>
          )}
        </div>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        title={
          <div className="mobile-drawer-header">
            <span className="brand-text">Traffix</span>
            <span className="user-greeting">
              Welcome, {user?.name || "User"}
            </span>
          </div>
        }
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
        className="mobile-navigation-drawer"
      >
        <MenuContent isMobile={true} />
      </Drawer>
    </>
  );
};

export default Navbar;
